import { useEffect, useMemo } from 'react';
import { cloneDeep } from 'lodash';
import { DensityPlotAnalysisV2 } from 'api/data/analyses';
import {
	AnalysisViewContainer,
	AnalysisErrorWrapper,
	AnalysisExportWrapper
} from 'components/Analysis/Analyses';
import { ExportFileNames } from 'types/index';
import { ZingChartExportTypes } from 'types/charts';
import { DensityPlotChartV2 } from './DensityPlotChartV2';
import { VariablesData } from 'store/data/variables';
import { AnalysisChartContainer } from '../UI';
import { exportSvg } from 'helpers/analysis';
import { getExportFileName } from 'helpers/generic';
import { mapVariableCategoryValueToLabel } from 'helpers/variables';
import {
	useAnalysesActiveColum,
	useFullscreenAnalysis,
	useProject,
	useTranslation,
	useVariablesDataSelectItems
} from 'hooks/store';
import { AnalysisType } from 'api/data/analyses/constants';

interface Props {
	analysis: DensityPlotAnalysisV2;
	variablesData: VariablesData;
	exporting: boolean;
	loading: boolean;
}

export function DensityPlotViewV2({ analysis, variablesData, exporting, loading }: Props) {
	const [{ data: project }] = useProject();
	const { translate } = useTranslation();

	const { selectItemsLabelMap } = useVariablesDataSelectItems(variablesData, {
		series: analysis.input.series,
		omitVariables: []
	});

	const {
		id,
		input,
		options: { configPanel, chartLegend = false, histogram = false },
		output: {
			dataset: { data: dataset }
		}
	} = analysis;
	const { variablesMap } = variablesData;
	const [activeColumn] = useAnalysesActiveColum();

	const [fullscreen] = useFullscreenAnalysis();
	const densityPlotChartId = ZingChartExportTypes.DensityPlot + id;

	useEffect(() => {
		if (exporting && project) {
			exportSvg(
				densityPlotChartId,
				getExportFileName(
					ExportFileNames.DensityPlotChart,
					project.projectId,
					project.projectName
				)
			);
		}
	}, [exporting, project]);

	const { parsedDataset } = useMemo(() => {
		let parsedDataset = cloneDeep(dataset ?? []);

		if (analysis.options.showCategoryLabels) {
			parsedDataset = parsedDataset?.map(result => {
				result.groupingyValue =
					(input.variables.groupVariables?.[0]?.name &&
						mapVariableCategoryValueToLabel(
							result.groupingyValue,
							variablesMap[input.variables.groupVariables?.[0]?.name]
						)) ??
					result.groupingyValue;
				return result;
			});
		}
		return { parsedDataset };
	}, [
		dataset,
		variablesMap,
		input.variables.groupVariables?.[0]?.name,
		analysis.options.showCategoryLabels
	]);

	const legendHeader = input.variables.groupVariables?.[0]?.name
		? selectItemsLabelMap[input.variables.groupVariables?.[0]?.name]
		: undefined;

	const scalesLabels = {
		labelX: selectItemsLabelMap[input.variables.numericVariable?.name ?? ''],
		labelY: translate(dict => dict.analysis.analyses.densityPlot.yAxis)
	};
	return (
		<AnalysisViewContainer isConfigPanelOpen={configPanel.open} loading={loading}>
			<AnalysisErrorWrapper analysis={analysis} analysisType={AnalysisType.DensityPlotV2}>
				<AnalysisChartContainer fullscreen={fullscreen} activeColumn={activeColumn}>
					<DensityPlotChartV2
						data={parsedDataset}
						isLegendEnabled={chartLegend}
						isHistogramEnabled={histogram}
						legendHeader={legendHeader}
						scalesLabels={scalesLabels}
						isConfigPanelOpen={configPanel.open}
					/>
				</AnalysisChartContainer>
				<AnalysisExportWrapper>
					<DensityPlotChartV2
						id={densityPlotChartId}
						data={parsedDataset}
						isLegendEnabled={chartLegend}
						isHistogramEnabled={histogram}
						legendHeader={legendHeader}
						isForExport
						scalesLabels={scalesLabels}
						isConfigPanelOpen={configPanel.open}
					/>
				</AnalysisExportWrapper>
			</AnalysisErrorWrapper>
		</AnalysisViewContainer>
	);
}
